import { goBackendApi } from 'hooks/api/httpApi';
import { createContext, ReactNode, useContext } from 'react';
import {
    flattenEdges,
    gatewayUrlWithApiVersion,
    GraphQLResponse,
    OperationVariables,
    SHOPIFY_API_VERSION,
    ShopifyGraphQLError,
    ShopifyGraphQLOptions,
} from './shopify-graphql.utils';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

/**
 * Minimal transport interface — keeps the gateway decoupled from a concrete HTTP client
 * while letting the app inject its auth-wired one (goBackendApi adapter by default).
 */
export interface ShopifyGraphQLGatewayAxios {
    post<T = unknown>(url: string, data?: unknown): Promise<{ data: T; status: number }>;
}

export interface ShopifyGraphQLGatewayConfig {
    axios: ShopifyGraphQLGatewayAxios;
    /** URL of the gateway Shopify GraphQL proxy endpoint (relative to the transport's base URL). */
    url: string;
}

export const ShopifyGraphQLGatewayContext = createContext<ShopifyGraphQLGatewayConfig | null>(null);

export const ShopifyGraphQLGatewayProvider = ({
    config,
    children,
}: {
    config: ShopifyGraphQLGatewayConfig | null;
    children: ReactNode;
}) => {
    return <ShopifyGraphQLGatewayContext.Provider value={config}>{children}</ShopifyGraphQLGatewayContext.Provider>;
};

export function useShopifyGraphQLGateway(): ShopifyGraphQLGatewayConfig | null {
    return useContext(ShopifyGraphQLGatewayContext);
}

/**
 * Env-driven gateway config: returns the backend proxy config when
 * VITE_APP_SHOPIFY_GRAPHQL_MODE=proxy, otherwise null (direct App Bridge transport).
 * Usable outside React (thunks, services) — this is the single source of truth the
 * ShopifyGraphQLGatewaySwitch provider and shopifyGraphQL() both read.
 */
export function getEnvShopifyGraphQLGatewayConfig(): ShopifyGraphQLGatewayConfig | null {
    if (import.meta.env.VITE_APP_SHOPIFY_GRAPHQL_MODE !== 'proxy') return null;
    return {
        axios: {
            post: async <T,>(url: string, data?: unknown) => {
                const response = await goBackendApi().post<T>(url, data as object);
                return { data: response.getData(), status: response.status as unknown as number };
            },
        },
        url: `/api/v1/proxy/shopify/graphql/${SHOPIFY_API_VERSION}`,
    };
}

function isThrottledGraphQLError(json: GraphQLResponse<unknown>): boolean {
    return (
        json.errors?.some(
            (error) =>
                error.message.toLowerCase().includes('throttled') || error.message.toLowerCase().includes('rate limit')
        ) ?? false
    );
}

function isRateLimitedTransportError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { response?: { status?: number } }).response?.status === 429
    );
}

function getRetryDelay(attempt: number): number {
    const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt);
    const jitter = Math.random() * 500;
    return exponentialDelay + jitter;
}

async function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Runs a Shopify Admin GraphQL operation through the gateway proxy instead of App Bridge.
 * The proxy is a passthrough: it accepts { query, variables } and returns the raw Shopify
 * GraphQL response, so error handling and edge flattening mirror shopifyGraphQL().
 */
export async function gatewayShopifyGraphQL<
    TData = unknown,
    TVariables extends OperationVariables = OperationVariables,
>(
    config: ShopifyGraphQLGatewayConfig,
    query: string,
    variables?: TVariables,
    options?: ShopifyGraphQLOptions
): Promise<TData> {
    let lastError: ShopifyGraphQLError | null = null;
    // The proxy carries the API version as the last segment of its URL, so an operation that needs a
    // different schema swaps that segment rather than getting its own gateway config.
    const url = gatewayUrlWithApiVersion(config.url, options?.version);

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        let json: GraphQLResponse<TData>;
        try {
            const response = await config.axios.post<GraphQLResponse<TData>>(url, { query, variables });
            json = response.data;
        } catch (error) {
            if (isRateLimitedTransportError(error) && attempt < MAX_RETRIES) {
                lastError = new ShopifyGraphQLError('Rate limited', undefined, true);
                await sleep(getRetryDelay(attempt));
                continue;
            }
            // Non-throttle transport errors (401, 5xx, network) are rethrown untouched so the
            // app's interceptors (e.g. token refresh) keep working.
            throw error;
        }

        if (isThrottledGraphQLError(json) && attempt < MAX_RETRIES) {
            lastError = new ShopifyGraphQLError(json.errors?.[0]?.message || 'Rate limited', json.errors, true);
            await sleep(getRetryDelay(attempt));
            continue;
        }

        if (json.errors?.length) {
            throw new ShopifyGraphQLError(json.errors[0].message, json.errors);
        }

        return flattenEdges(json.data) as TData;
    }

    throw lastError ?? new ShopifyGraphQLError('Max retries exceeded due to rate limiting', undefined, true);
}
